/**
 * Zahlungsabgleich (sevdesk-Logik) — reine Funktionen, getestet.
 * Die Buchung selbst macht die RPC fibu_reconcile; hier nur Vorschläge,
 * Differenz-Regeln und Kennzahlen für die Oberfläche.
 */
import { hasInvoiceToken } from './bankMatching.js';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Noch zuzuordnender Betrag eines Umsatzes (immer positiv) */
export function txRest(tx) {
  return round2(Math.abs(Number(tx?.amount) || 0) - (Number(tx?.allocated_amount) || 0));
}

export const isIncoming = (tx) => Number(tx?.amount) >= 0;

/** Status eines Umsatzes für Liste/Badge */
export function txStatus(tx) {
  const s = tx?.reconciliation_status;
  if (s === 'deleted' || tx?.deleted_at) return 'geloescht';
  if (s === 'matched') return 'gebucht';
  if (s === 'ignored') return 'ignoriert';
  if (s === 'partial') return 'teilweise';
  return 'offen';
}

export const TX_STATUS_LABEL = {
  offen: 'Offen', teilweise: 'Teilweise', gebucht: 'Gebucht', ignoriert: 'Ignoriert', geloescht: 'Gelöscht',
};

/**
 * Differenz zwischen Umsatz-Rest und gewählten Posten.
 * @param {number} rest   offener Umsatzbetrag
 * @param {{amount: number}[]} selections  zu begleichende Beträge je Posten
 * @returns {{ sum: number, diff: number, kind: 'leer'|'exakt'|'weniger'|'mehr' }}
 *   weniger = Zahlung kleiner als die Posten, mehr = Zahlung größer
 */
export function computeDifference(rest, selections) {
  const sum = round2((selections || []).reduce((s, x) => s + (Number(x.amount) || 0), 0));
  if (!selections?.length) return { sum: 0, diff: round2(rest), kind: 'leer' };
  const diff = round2(rest - sum);
  if (Math.abs(diff) < 0.005) return { sum, diff: 0, kind: 'exakt' };
  return { sum, diff, kind: diff > 0 ? 'mehr' : 'weniger' };
}

/**
 * Erlaubte Begründungen je Differenzart (wie sevdesk „Differenz begründen“).
 * @returns {{ key: 'partial'|'skonto'|'fees', label: string, hint: string }[]}
 */
export function differenceReasons(kind, incoming) {
  if (kind === 'weniger') {
    return [
      { key: 'partial', label: 'Teilzahlung / Teil einer Sammelzahlung', hint: 'Der Rest bleibt offen — Status „Teilbezahlt“ bis alles bezahlt ist.' },
      { key: 'skonto', label: 'Skonto', hint: incoming ? 'Gewährter Skonto; Umsatzsteuer wird anteilig korrigiert.' : 'Erhaltener Skonto; Vorsteuer wird anteilig korrigiert.' },
      { key: 'fees', label: 'Kosten des Zahlungsverkehrs (steuerfrei)', hint: 'Differenz auf 6855 Nebenkosten des Geldverkehrs.' },
    ];
  }
  if (kind === 'mehr') {
    return [
      { key: 'partial', label: 'Teil einer Sammelzahlung', hint: 'Der Restbetrag des Umsatzes bleibt offen und kann weiteren Belegen zugeordnet werden.' },
      { key: 'fees', label: 'Kosten des Zahlungsverkehrs (steuerfrei)', hint: 'Differenz auf 6855 Nebenkosten des Geldverkehrs.' },
    ];
  }
  return [];
}

/** Kategorien „ohne Beleg“ (Drei-Punkte-Menü + Zuordnen-Dialog) */
export const ACCOUNT_CATEGORIES = [
  { key: 'kredit', label: 'Rückzahlung eines Kredites', short: 'Kredit', hint: 'Tilgung auf 3150, optional Zinsanteil auf 7320.' },
  { key: 'gesellschafter', label: 'Privatentnahme / Gesellschafter', short: 'Privat', hint: 'Gesellschafter-Verrechnungskonto (in den Einstellungen festlegen).' },
  { key: 'transit', label: 'Geldbewegung (Geldtransit)', short: 'Geldtransit', hint: 'z. B. PayPal → Hauptkonto, Kasse → Bank. Beide Seiten über 1460.' },
  { key: 'gebuehren', label: 'Gebühren für Zahlungsverkehr', short: 'Gebühren', hint: 'Steuerfrei auf 6855.' },
];

const norm = (s) => String(s || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, ' ').trim();

/** Wortüberlappung Name ↔ Gegenpartei (0…1) */
export function nameSimilarity(a, b) {
  const wa = new Set(norm(a).split(' ').filter((w) => w.length > 2));
  const wb = new Set(norm(b).split(' ').filter((w) => w.length > 2));
  if (!wa.size || !wb.size) return 0;
  let hit = 0;
  wa.forEach((w) => { if (wb.has(w)) hit++; });
  return hit / Math.min(wa.size, wb.size);
}

/**
 * Vorschläge für einen Umsatz: passende Richtung, nach Punkten sortiert.
 * Rechnungsnr. +50, Betrag = offen +40, Skontobetrag +30, Name +20.
 * @returns {{ item: object, score: number, reasons: string[] }[]}
 */
export function rankCandidates(tx, openItems, { limit = 5, minScore = 20 } = {}) {
  const type = isIncoming(tx) ? 'receivable' : 'payable';
  const rest = txRest(tx);
  return (openItems || [])
    .filter((i) => i.type === type && ['open', 'partial'].includes(i.status) && Number(i.open_amount) > 0)
    .map((item) => {
      let score = 0;
      const reasons = [];
      if (item.invoice_no && hasInvoiceToken(tx.purpose, item.invoice_no)) { score += 50; reasons.push('Belegnummer'); }
      const open = Number(item.open_amount);
      if (Math.abs(open - rest) < 0.005) { score += 40; reasons.push('Betrag'); }
      else if (item.skonto_amount && Math.abs(open - Number(item.skonto_amount) - rest) < 0.005) { score += 30; reasons.push('Betrag mit Skonto'); }
      if (nameSimilarity(item.partner_name, tx.counterparty_name) >= 0.5) { score += 20; reasons.push('Name'); }
      return { item, score, reasons };
    })
    .filter((c) => c.score >= minScore)
    .sort((a, b) => b.score - a.score || Number(a.item.open_amount) - Number(b.item.open_amount))
    .slice(0, limit);
}

/** Freitextsuche über offene Posten (Nummer, Name, Betrag) */
export function searchOpenItems(openItems, query, incoming) {
  const type = incoming ? 'receivable' : 'payable';
  const q = norm(query);
  const amount = Number(String(query || '').replace(/\./g, '').replace(',', '.'));
  return (openItems || [])
    .filter((i) => i.type === type && ['open', 'partial'].includes(i.status) && Number(i.open_amount) > 0)
    .filter((i) => !q
      || norm(i.invoice_no).includes(q)
      || norm(i.partner_name).includes(q)
      || (Number.isFinite(amount) && amount > 0 && Math.abs(Number(i.open_amount) - amount) < 0.005));
}

// ── Abgleichsquote ──────────────────────────────────────────────

/** Quote in % (Anzahl verbuchter / alle); ohne Umsätze = 100 */
export function quotePercent(stats) {
  const total = Number(stats?.total) || 0;
  if (!total) return 100;
  return Math.floor(((Number(stats.done) || 0) / total) * 1000) / 10;
}

/** < 80 rot, 80–99,9 gelb, 100 grün */
export function quoteLevel(percent) {
  if (percent >= 100) return 'gruen';
  if (percent >= 80) return 'gelb';
  return 'rot';
}

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const PERIODS = [
  { key: 'monat', label: 'Dieser Monat' },
  { key: 'vormonat', label: 'Letzter Monat' },
  { key: 'quartal', label: 'Quartal' },
  { key: 'jahr', label: 'Jahr' },
  { key: '90', label: '90 Tage' },
];

/** Zeitraum → { from, to } (ISO-Datum, lokal) */
export function periodRange(key, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case 'vormonat': return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'quartal': { const q = Math.floor(m / 3) * 3; return { from: iso(new Date(y, q, 1)), to: iso(now) }; }
    case 'jahr': return { from: iso(new Date(y, 0, 1)), to: iso(now) };
    case '90': return { from: iso(new Date(y, m, now.getDate() - 89)), to: iso(now) };
    default: return { from: iso(new Date(y, m, 1)), to: iso(now) };
  }
}
